import { SchedulePage } from "@/components/schedule/schedule-page";
import { ScheduleSettingsUI } from "@/components/settings/schedule-settings";
import { ScheduleSchema, User, useUpdateUserSchedule } from "@/lib/api/user";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { ENABLE_MENTOR_SCHEDULE_FLAG } from "@/lib/posthog-events";
import { getUserTimezoneWithDefault } from "@/lib/utils/timezone";
import { useFeatureFlagEnabled } from "@posthog/react";

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

interface UserMentorScheduleProps {
  user: User;
}

export function UserMentorSchedule({ user }: UserMentorScheduleProps) {
  const newSchedule =
    useFeatureFlagEnabled(ENABLE_MENTOR_SCHEDULE_FLAG) || import.meta.env.DEV;
  const ability = useAbility();
  const canUpdate =
    hasPrivilegedAccessFromAbility(ability) && ability.can("update", "users");
  const { mutateAsync: updateScheduleMutation, isPending } =
    useUpdateUserSchedule(user.id);

  if (newSchedule) {
    return <SchedulePage userId={user.id} disabled={!canUpdate} />;
  }

  const defaultValues: ScheduleSchema = {
    schedule:
      user.metadata?.schedule ||
      daysOfWeek.map((day) => {
        if (
          day === "Monday" ||
          day === "Tuesday" ||
          day === "Wednesday" ||
          day === "Thursday"
        ) {
          return { day, isOnline: true, startTime: "17:00", endTime: "22:00" };
        }
        if (day === "Friday") {
          return { day, isOnline: true, startTime: "17:00", endTime: "19:00" };
        }
        return { day, isOnline: false, startTime: "17:00", endTime: "19:00" };
      }),
    timezone: getUserTimezoneWithDefault(user),
    autoDetectTimezone: user.metadata?.auto_detect_timezone ?? true,
  };

  return (
    <ScheduleSettingsUI
      defaultValues={defaultValues}
      onSubmit={updateScheduleMutation}
      isPending={isPending}
      userName={user.name ?? undefined}
      disabled={!canUpdate}
    />
  );
}
